import React from 'react';
import type { CanvasSectionBlock } from '../sectionRegistry';
import type { EditorBoundsBox } from './editorBounds';

type SelectionGroupBoundsProps = {
  blocks: CanvasSectionBlock[];
  editorBoundsByBlockId: Record<string, EditorBoundsBox>;
  columnWidthPx: number;
  rowHeightPx: number;
};

function resolveBlockBounds(
  block: CanvasSectionBlock,
  editorBoundsByBlockId: Record<string, EditorBoundsBox>,
  columnWidthPx: number,
  rowHeightPx: number
) {
  const editorBounds = editorBoundsByBlockId[block.id];
  const activeBox = editorBounds?.activeBox ?? editorBounds?.frameBox;
  if (activeBox) {
    return {
      left: activeBox.left,
      top: activeBox.top,
      right: activeBox.right,
      bottom: activeBox.bottom,
    };
  }
  const left = (block.layout.x - 1) * columnWidthPx;
  const top = (block.layout.y - 1) * rowHeightPx;
  const width = block.layout.w * columnWidthPx;
  const height = block.layout.h * rowHeightPx;
  return {
    left,
    top,
    right: left + width,
    bottom: top + height,
  };
}

export function SelectionGroupBounds({
  blocks,
  editorBoundsByBlockId,
  columnWidthPx,
  rowHeightPx,
}: SelectionGroupBoundsProps) {
  if (blocks.length < 2) return null;

  const bounds = blocks.map((block) =>
    resolveBlockBounds(block, editorBoundsByBlockId, columnWidthPx, rowHeightPx)
  );
  const left = Math.min(...bounds.map((box) => box.left));
  const top = Math.min(...bounds.map((box) => box.top));
  const right = Math.max(...bounds.map((box) => box.right));
  const bottom = Math.max(...bounds.map((box) => box.bottom));
  const width = Math.max(1, right - left);
  const height = Math.max(1, bottom - top);

  return (
    <div
      className="pointer-events-none absolute z-[24] rounded-xl border-2 border-dashed border-blue-500/75 bg-blue-400/6 shadow-[inset_0_0_0_1px_rgba(59,130,246,0.18)]"
      style={{ left, top, width, height }}
      data-builder-selection-group-bounds="true"
    />
  );
}
